import { useEffect, useEffectEvent } from 'react';
import { useAuthSession } from '@/entities/user/session';
import { LoginStatus } from '@/entities/user/user';
import type { MainWsMessage } from '@/shared/api/websocket';
import type { WsTypes } from '@/shared/api/websocket-events';
import { runSocketCallback } from './run-callback';
import { useWebSocketStore } from './socket-store';

export { useWebSocketStore } from './socket-store';

interface UseSocketParams {
  enabled?: boolean;
  type: WsTypes;
  handler: (message: MainWsMessage) => unknown;
  name?: string;
  onReconnect?: () => unknown;
}

export const useSocket = ({
  enabled = true,
  type,
  handler,
  name,
  onReconnect,
}: UseSocketParams) => {
  const { loginStatus } = useAuthSession();
  const active = enabled && loginStatus === LoginStatus.login;
  const handleMessage = useEffectEvent(handler);
  const handleReconnect = useEffectEvent(() =>
    runSocketCallback(() => onReconnect?.()),
  );

  useEffect(() => {
    if (!active) return;

    const { addHandler, removeHandler } = useWebSocketStore.getState();
    const callback = (message: MainWsMessage) => handleMessage(message);
    addHandler(type, callback, name);

    return () => removeHandler(type, callback);
  }, [type, name, active]);

  useEffect(() => {
    if (!active) return;

    // Observe every transition directly: React can batch close/open updates.
    const unsubscribe = useWebSocketStore.subscribe((state, previous) => {
      if (state.isConnected && !previous.isConnected) {
        void handleReconnect();
      }
    });
    const release = useWebSocketStore.getState().retainConnection();

    return () => {
      unsubscribe();
      release();
    };
  }, [active]);
};